import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { EnvironmentKind, environmentLabel } from '../../../admin-v2/shell/environment-label';

/** Written on <html> outside production; it is what gives --jft-envbar-h its height. */
export const ENVIRONMENT_ATTRIBUTE = 'data-jft-env';

const BARS: Record<Exclude<EnvironmentKind, 'production'>, { text: string }> = {
    local: { text: 'Development environment' },
    staging: { text: 'Staging environment' },
    sandbox: { text: 'Sandbox environment' },
};

/** The bar for a build's API host, or null in production. */
export function environmentBar(apiUrl: string): { kind: EnvironmentKind; text: string } | null {
    const kind = environmentLabel(apiUrl).kind;
    return kind === 'production' ? null : { kind, ...BARS[kind] };
}

// A full-width bar naming the environment, above every route (login and /admin-v2 included),
// everywhere but production. The words carry the meaning, never the colour alone.
// It takes real height (--jft-envbar-h) instead of floating over the page, so nothing under it
// is covered; the fixed panels under the navbar add the same token to --navbar-height.
@Component({
    selector: 'jft-env-banner',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
      @if (bar) {
        <div class="env-bar" [class.env-bar--sandbox]="bar.kind === 'sandbox'" role="status" data-testid="env-banner">
          <span>{{ bar.text }}</span>
        </div>
      }`,
    styleUrl: './env-banner.component.scss',
})
export class EnvBannerComponent {
    readonly bar = environmentBar(environment.apiUrl);

    constructor() {
        const root = inject(DOCUMENT).documentElement;
        if (this.bar) {
            root.setAttribute(ENVIRONMENT_ATTRIBUTE, this.bar.kind);
        } else {
            root.removeAttribute(ENVIRONMENT_ATTRIBUTE);
        }
    }
}
